/**
 * bb-cart-addons.js — the order check in the slide cart and on /cart.
 *
 * ONE dismissible card, up to three prompts, all driven by what is actually in the cart:
 *   1. POSTS   — BOM shortfall: one per panel + one end post + one per gate, minus what you have.
 *   2. GATES   — family-matched, height-matched, 4ft and up.
 *   3. CONVERT — loose -> framed (upgrade) or framed -> loose (save), on PRODUCT price.
 *
 * WHY THE CART: it knows the PANEL COUNT, so the post BOM can actually be applied. The PDP cannot
 * know the run length. That turns a vague upsell into arithmetic.
 *
 * ⛔⛔ TRAPS — every one of these was hit for real on this store:
 *  1. NEVER count .CartItem nodes. The drawer renders 3 on an EMPTY cart ("You might like these"
 *     recommendation tiles). /cart.js is the only truth.
 *  2. The drawer DESTROYS its inner DOM on every change (_rerenderCart -> _replaceContent), so
 *     anything injected vanishes. Re-inject from a debounced body MutationObserver with an
 *     idempotence flag — the pattern bb-cart-swap.js already proved here.
 *  3. "corruGATEd" CONTAINS "gate". Word boundary only: /\bgates?\b/i. And exclude "hardware" —
 *     CA's gates collection carries a Universal Black Gate Hardware Kit that is not a gate.
 *  4. products.json LIES about availability (reports 0 available for everything, ignoring
 *     CONTINUE). Use it for handles/titles ONLY; read real availability from /products/x.js.
 *  5. Posts must REACH: length = panel height + 3ft. Mixed carts size to the TALLEST panel.
 *  6. ZM kits ship WITH posts -> gates yes, posts no. They need a SEPARATE height, or a ZM panel
 *     would size posts for a run that needs none.
 *  7. Converting flips shipping_mode and therefore the DELIVERY LANE. Quoting only the product
 *     delta understates the upgrade under $2,500.
 *  8. Fail QUIET. A wrong "you need 9 posts" is worse than no prompt.
 */
(function () {
  'use strict';

  var FLAG = 'data-bb-addons-rendered';
  var DISMISS_KEY = 'bbCartAddonsDismissed';
  var POST_HANDLES = { Metal: 'metal-fence-posts-for-wood-fence', Wood: 'wood-fence-posts' };
  var CAP_HANDLE = 'fence-post-caps';                    /* caps: wood posts only */
  var HW_HANDLE  = 'universal-black-gate-hardware-kit';  /* hardware: gates only */
  /* post savers: CEDAR wood posts only. Sold as a 10-PACK — one unit covers ten posts. */
  var SAVER_HANDLE = 'products-barrierguard-groundline-sleeve';
  var EXCLUDE_PANEL = /\bgates?\b|post|hardware|topper|planter|sample|roof|siding|sauna/i;
  var ZM = /zero[- ]maintenance/i;
  var MIN_GATE_H = 4;          /* no 3ft gate exists in the catalogue on either store */
  /* ⛔ BB-MINORDER-FIXED — THE $500 ORDER MINIMUM NO LONGER EXISTS. The four-band freight card
     ($149/$249/$349/$449) carries no minimum and no free tier, so the claim that once stood here
     — that a cart below this figure could not be shipped — is false. Corrected 2026-10-06.
     ⭐ THE CONSTANT STILL EARNS ITS PLACE, for ONE job: it suppresses the convert DOWNGRADE on a
     small cart (its single use, below). It is NOT a card-wide gate — every prompt renders at any
     subtotal.
     ⚠️ SUPERSEDED, kept for the history: owner 2026-08-31 asked for "it shouldnt show those prompts if the sub total is under $500 in cart".
     A later change removed that gate deliberately — the shopper under the old floor was the one
     who most needed telling what to add.
     Same figure in each store's own currency (USD on USA, CAD on CA), so one constant serves. */
  var MIN_ORDER = 50000;       /* cents */

  /* ⛔ THIS CARD SAYS NOTHING ABOUT DELIVERY. The PDP owns that story, using the shopper's
     own typed ZIP. If delivery ever returns here, do NOT hardcode a rate: the flat card covers the
     STANDARD zones only, and the extended zones are per-zone ladders many times larger. The
     estimator publishes the real values live — read those. Detail is in the session report under
     "What the freight work established", kept out of this file so nothing here reads as live. */

  /* panel title -> gate family keyword. Order matters: ZM before the generic metal families. */
  /* ⭐ ORDER MATTERS AND SPECIFICITY COMES FIRST. Since the ZM hog wire gate was split into its
     own product (2026-09-03) two gates match /zero[- ]maintenance/ and two match /hog wire/, so
     the generic rows below carry a negative lookahead. Without it familyGate() takes gates[0]
     and the winner is decided by COLLECTION ORDER — a hog wire kit was being sent to the
     solid-infill ZM gate, which is the exact colour conflict the split existed to remove. */
  var FAMILY = [
    /* ZM hog wire: its own product, must be claimed before the generic ZM row sees it */
    [/zero[- ]maintenance.*hog wire|hog wire.*zero[- ]maintenance/i, /zero[- ]maintenance.*hog wire/i],
    /* ⭐ CATTLE BEFORE THE GENERIC ZM ROW (2026-10-14). "Zero-Maintenance Black Cattle
       Panel Fence Kit" matches /zero[- ]maintenance/, so the generic row claimed it and sent a
       6-gauge welded mesh fence to the SOLID-infill gate — which has no cattle Metal Style and
       no Silver. The negative lookahead on the generic row keeps the table order-independent. */
    [/cattle/i,                                   /cattle/i],
    /* generic ZM = the SOLID-infill palette; never answers for a hog wire OR a cattle cart */
    [/zero[- ]maintenance(?!.*cattle)|full metal fence kit/i, /zero[- ]maintenance(?!.*hog wire)/i],
    /* a plain hog wire panel takes the WOOD-FRAMED gate, never the zero-maintenance one */
    [/hog wire/i,                                 /^(?!.*zero[- ]maintenance).*hog wire/i],
    [/ribbed|deep ?groove/i,                      /ribbed/i],
    [/corrugated|high[- ]wind|corten/i,           /corrugated/i],
    [/aluminum|aluminium/i,                       /aluminum/i]
  ];

  var cache = {};
  var gateList = null;
  var busy = false;

  function money(c) {
    try {
      return (window.theme && theme.Currency && theme.moneyFormat)
        ? theme.Currency.formatMoney(c, theme.moneyFormat)
        : '$' + (c / 100).toFixed(2);
    } catch (e) { return '$' + (c / 100).toFixed(2); }
  }
  /* Drop the cents when they are .00 — "$165.00 a panel" is noisier than "$165 a panel", and
     these are round figures nine times out of ten. Keeps cents when they actually exist. */
  function money0(c) {
    var m = money(c);
    return (c % 100 === 0) ? m.replace(/([.,])00\b/, '') : m;
  }
  function heightOf(s) { var m = String(s || '').match(/(\d+)\s*'/); return m ? parseInt(m[1], 10) : NaN; }
  function speciesOf(s) {
    var t = String(s || '').toLowerCase();
    if (t.indexOf('cedar') > -1) return 'cedar';
    if (t.indexOf('treated') > -1) return 'treated';
    return '';
  }
  /* ⛔⛔ COLOUR IS DECIDED BY THE DOMINANT LINE, NOT THE FIRST ONE.
     It used to be `if (!s.colour) s.colour = ...`, so any earlier line carrying a colour — a
     leftover brown panel from a previous test, a deck railing — locked the colour for the whole
     cart and TEN black panels behind it could not override it. That is exactly how a black run
     ended up matched to a brown gate. `!s.colour` answers "have we seen a colour yet"; the real
     question is "what is this run made of". Most units wins; ties keep the earlier line, so a
     single-line cart behaves exactly as before. */
  function noteColour(s, c, qty) {
    if (!c) return;
    var n = qty || 0;
    if (!s.colour || n > (s.colourQty || 0)) { s.colour = c; s.colourQty = n; }
  }
  /* ⭐ The panel's WIRE OPENING ("4\" x 4\""). The GATE product has no such option — Color, Panel
     Size and Frame Type only — so this can never be matched at variant level. The PDP bundle
     solves it by writing the opening onto the gate LINE, and this is the reader that lets the
     slide cart do the same. Scanned by SHAPE across all segments, never by position: the opening
     sits second on the panels but the axis order differs per product. */
  function openingOf(t) {
    var parts = String(t || '').split(/\s*\/\s*/);
    for (var i = 0; i < parts.length; i++) {
      /* inches, not feet — the SIZE axis uses \' and would otherwise match here */
      var m = parts[i].match(/\d+\s*"\s*x\s*\d+\s*"/);
      if (m) return m[0].replace(/"/g, 'in').replace(/\s+/g, ' ').trim();
    }
    return '';
  }
  /* ⛔ COLOUR MATTERS AS MUCH AS SPECIES. A Black panel run with a Silver gate is the wrong
     product, not a cheaper one. The panel carries its colour in the PRODUCT TITLE ("Black Hog Wire
     Fence Panels"); the gate carries it in the FIRST segment of the variant title
     ("Silver / 4' x 4' (Man Gates) / Full Metal Frame"). Match on a keyword, because the gate says
     "Midnight Black (Most Popular)" where the panel just says "Black". */
  function colourOf(str) {
    var t = String(str || '').toLowerCase();
    if (/\bblack\b/.test(t)) return 'black';
    if (t.indexOf('silver') > -1) return 'silver';
    if (t.indexOf('brown') > -1 || t.indexOf('coffee') > -1) return 'brown';
    if (t.indexOf('rust') > -1 || t.indexOf('patina') > -1 || t.indexOf('corten') > -1) return 'rust';
    if (/\bgreen\b/.test(t)) return 'green';
    return '';
  }
  /* Same reason: the colour is not always the first segment. Take the first segment that yields
     a known colour AND is not the size. */
  function gateColour(v) {
    var parts = String(v.title || '').split(/\s*\/\s*/);
    for (var i = 0; i < parts.length; i++) {
      if (/(\d+)\s*'\s*x\s*(\d+)\s*'/.test(parts[i])) continue;   /* that is the size */
      var c = colourOf(parts[i]);
      if (c) return c;
    }
    return '';
  }

  function isLoose(s) { return /unframed/i.test(String(s || '')); }
  function isFramed(s) { return !isLoose(s) && /treated|cedar/i.test(String(s || '')); }

  function isDismissed() { try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; } }
  function setDismissed(v) {
    try { v ? localStorage.setItem(DISMISS_KEY, '1') : localStorage.removeItem(DISMISS_KEY); } catch (e) {}
  }

  /* ── per-section dismissal ────────────────────────────────────────────────────────────────
     Owner 2026-08-31: "they should be able to close that stuff as well". Someone who has their
     own posts should be able to kill just that row and still see the gate — closing the whole
     card to silence one line is a blunt instrument.
     Stored as a comma list so one key covers all three. Same guarded access as everything else:
     localStorage THROWS in private windows rather than returning null. */
  var SEC_KEY = 'bbCartAddonsSecs';
  function dismissedSecs() {
    try { return (localStorage.getItem(SEC_KEY) || '').split(',').filter(Boolean); } catch (e) { return []; }
  }
  function dismissSec(name) {
    try {
      var l = dismissedSecs();
      if (l.indexOf(name) < 0) l.push(name);
      localStorage.setItem(SEC_KEY, l.join(','));
    } catch (e) {}
  }
  function undismissSec(name) {
    try {
      localStorage.setItem(SEC_KEY, dismissedSecs().filter(function (x) { return x !== name; }).join(','));
    } catch (e) {}
  }
  function clearSecs() { try { localStorage.removeItem(SEC_KEY); } catch (e) {} }

  function getProduct(handle) {
    if (cache[handle] !== undefined) return Promise.resolve(cache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { cache[handle] = j; return j; })
      .catch(function () { cache[handle] = null; return null; });
  }

  /* Resolve gates from the live collection so handles are never hardcoded. They happen to match
     across both stores today, but that is not a guarantee worth baking in. */
  function getGates() {
    if (gateList) return Promise.resolve(gateList);
    return fetch('/collections/gates/products.json?limit=50', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        gateList = (j && j.products ? j.products : []).filter(function (p) {
          return /\bgates?\b/i.test(p.title) && !/hardware/i.test(p.title);
        });
        return gateList;
      })
      .catch(function () { gateList = []; return gateList; });
  }

  function familyGate(panelTitle, gates) {
    for (var i = 0; i < FAMILY.length; i++) {
      if (FAMILY[i][0].test(panelTitle)) {
        var re = FAMILY[i][1];
        var hit = gates.filter(function (g) { return re.test(g.title); })[0];
        if (hit) return hit;
      }
    }
    return null;
  }

  function readCart(cart) {
    if (!cart || !cart.items) return null;
    var s = {
      panels: 0, gates: 0, posts: 0, zmPanels: 0,
      /* woodPostW is the WIDTH of the wood post already in the cart — it decides the cap size, */
      /* which is derived and never offered as a choice (a 4x4 post can only take a 4" cap).   */
      woodPosts: 0, woodPostW: 0, caps: 0, hardware: 0, capLines: [], hwLines: [], gateKits: 0,
      gatePosts: 0,   /* posts the gates in the cart demand, counted per KIND */
      maxH: 0, maxGateH: 0, zmH: 0, species: '',
      panelTitle: '', colour: '', colourQty: 0, loose: [], framed: [], gateKinds: {}, gateLines: [], postLines: [],
      subtotal: cart.total_price || 0, count: cart.item_count || 0
    };

    cart.items.forEach(function (i) {
      var pt = i.product_title || '';
      var vt = i.variant_title || i.title || '';
      var handle = i.handle || '';
      var q = i.quantity || 0;
      /* ⛔ Some products keep their SIZE in the PRODUCT TITLE, not the variant. The single-size
         hog wire panels ("Black Hog Wire Fence Panel — 4'x6'") sell only a Wire Opening option,
         so heightOf(vt) was NaN, maxGateH stayed 0, and those five products offered NO GATE and
         no post length at all. Found by sweeping every product through this file, 2026-09-04.
         ⛔ VARIANT FIRST — it is authoritative when it carries a size; this only fills the gap.
         3ft members remain excluded by MIN_GATE_H. */
      var h = heightOf(vt);
      if (isNaN(h)) h = heightOf(pt);

      /* ⛔ Caps and kits are matched on HANDLE and returned before anything else looks at them.
         Their titles would otherwise fall through into the panel counter, and the kit title
         ("Universal Black Gate Hardware Kit") also trips the gate test below. */
      /* ⛔ RECORD THE LINE, not only the count. These branches counted caps and hardware but
         never kept their ids, so "switch to loose" had nothing to remove — the shopper kept
         paying for caps whose posts had gone and hardware whose gate had gone. */
      if (handle === CAP_HANDLE) { s.caps += q; s.capLines.push({ id: i.variant_id, qty: q, price: i.price }); return; }
      if (handle === HW_HANDLE)  { s.hardware += q; s.hwLines.push({ id: i.variant_id, qty: q, price: i.price }); return; }
      if (handle === POST_HANDLES.Metal || handle === POST_HANDLES.Wood) {
        s.posts += q;
        if (handle === POST_HANDLES.Wood) {
          s.woodPosts += q;
          /* "Pressure Treated (Ready to Install) / 4 x 4 x 8" -> 4 */
          var wm = String(vt).match(/(\d+)\s*x\s*\d+\s*x\s*\d+/i);
          if (wm && !s.woodPostW) s.woodPostW = parseInt(wm[1], 10);
        }
        s.postLines.push({ id: i.variant_id, qty: q, price: i.price });
        return;
      }
      /* ⛔ "Universal Black Gate Hardware Kit" contains "Gate". Without the hardware exclusion it
         counted as a gate, and because the post BOM is panels + 1 + gates, every kit in the cart
         demanded one post the run does not need. familyGate() at line ~157 already guards this
         way; readCart did not. EXCLUDE_PANEL lists "hardware" but is tested below this branch,
         so it never got the chance. */
      if (/\bgates?\b/i.test(pt) && !/hardware/i.test(pt)) {
        s.gates += q;
        s.gateLines.push({ id: i.variant_id, qty: q, price: i.price });
        /* ⭐ A run often needs BOTH styles — owner 2026-08-31: "if i add a double swing gate i
           might need to add a man gate as well". Record which KINDS are present, not just the
           count, so the row keeps offering the style they have not taken. */
        var gk = gateKind({ title: vt });
        if (gk) s.gateKinds[gk] = true;
        /* ⭐ what these gates NEED, not how many there are — a double swing takes two kits */
        s.gateKits += kitsFor(gk) * q;
        s.gatePosts += postsFor(gk) * q;   /* 2 for a double swing, 1 for a man gate */
        return;
      }
      if (EXCLUDE_PANEL.test(pt)) return;

      if (ZM.test(pt + ' ' + vt)) {
        s.zmPanels += q;
        if (!isNaN(h) && h > s.maxGateH) s.maxGateH = h;
        /* ZM height kept SEPARATELY from maxH (owner 2026-09-09). maxH sizes the run's posts
           and a ZM kit needs none - but its GATE needs one, and that post still has to reach.
           Feeding maxH here would size posts for a run that ships with its own. */
        if (!isNaN(h) && h > s.zmH) s.zmH = h;
        if (!s.panelTitle) s.panelTitle = pt;
        /* ⛔ The ZM kit is the ONLY panel product whose TITLE carries no colour — it sells Color
           as a variant option ("4' x 6' / 4in x 4in / Brown"). This branch returned before the
           colour was ever read, so a BROWN or SILVER kit fell through to gateChoices' COL_ORDER
           default, which is black. VARIANT title first here, the opposite order to the panel
           branch, because that is where this product keeps it. */
        noteColour(s, colourOf(vt) || colourOf(pt), q);
        if (!s.wire) s.wire = openingOf(vt);
        return;
      }

      s.panels += q;
      if (!isNaN(h) && h > s.maxH) s.maxH = h;
      if (!isNaN(h) && h > s.maxGateH) s.maxGateH = h;
      if (!s.species) s.species = speciesOf(vt);
      if (!s.panelTitle) s.panelTitle = pt;
      noteColour(s, colourOf(pt) || colourOf(vt), q);
      /* ⛔ carried onto the gate line later — the gate cannot be mesh-matched, so the ORDER has to
         say it. Same rule the PDP bundle follows. */
      if (!s.wire) s.wire = openingOf(vt);

      var line = { key: i.key, id: i.variant_id, handle: handle, qty: q, price: i.price, title: pt, variant: vt };
      if (isLoose(vt)) s.loose.push(line);
      else if (isFramed(vt)) s.framed.push(line);
    });

    /* ⭐ Posts and gates only make sense once there is ASSEMBLED material in the cart.
       Owner 2026-08-31: "the gates shouldnt show up if theres no assembled in the cart.. nor
       should the posts", and "if they opt to swap to loose they should lose the posts and gates".
       A loose-material buyer is building their own frame — they are not in the market for our
       posts or a matching gate yet, and the only useful prompt for that cart is the upgrade.
       Because the card re-reads /cart.js after every swap, converting a cart to loose makes both
       rows disappear on their own — that transition needs no special case.
       ZM kits count as assembled: they ship framed, with their own posts. */
    s.hasAsm = (s.framed.length > 0 || s.zmPanels > 0);
    return s;
  }

  function pickPost(product, need, species) {
    if (!product || !product.variants) return null;
    var avail = product.variants.filter(function (v) { return v.available; });
    if (!avail.length) return null;
    /* ⛔⛔ NO \b AFTER THE APOSTROPHE. Metal post titles read: Midnight Black / True 4" x 4" / 9'
       — a word boundary cannot follow ' (both are non-word characters), so /(\d+)\s*(?:'|ft)\b/
       matched NOTHING and every metal length came back NaN. The row then reported
       "No metal post reaches a 4ft panel", which is FALSE — 6ft, 9ft and 12ft are all stocked.
       A parse failure printed as a business fact, which is exactly what must never happen. */
    function lenOf(v) {
      var t = String(v.title || '');
      var d = t.match(/(\d+)\s*(?:'|ft|feet)/);
      if (d) return parseInt(d[1], 10);
      var x = t.split(/\s*x\s*/);              /* wood: "4 x 4 x 8" dimensions */
      if (x.length >= 3) { var n = parseInt(x[x.length - 1], 10); if (!isNaN(n)) return n; }
      return NaN;
    }
    var bySp = avail.filter(function (v) { var sp = speciesOf(v.title); return !species || !sp || sp === species; });
    if (!bySp.length) bySp = avail;
    var fits = bySp.filter(function (v) { var L = lenOf(v); return !isNaN(L) && L >= need; })
      .sort(function (a, b) { return lenOf(a) - lenOf(b); });
    if (fits[0]) return { v: fits[0], per: 1 };

    /* ⭐ Owner 2026-08-31: "what they can do is take a 12' and cut it in half". On a short fence
       every stocked length can overshoot — a 3ft panel needs 6ft, and a 12ft post cuts into TWO
       of those. Only offered when the halves genuinely reach, and the note says so plainly so
       nobody meets a saw by surprise. */
    var halves = bySp.filter(function (v) { var L = lenOf(v); return !isNaN(L) && Math.floor(L / 2) >= need; })
      .sort(function (a, b) { return lenOf(a) - lenOf(b); });
    if (halves[0]) return { v: halves[0], per: 2 };
    return null;
  }

  function lenLabel(v) {
    var m = String(v.title || '').match(/(\d+)\s*(?:'|ft|feet)/);
    if (m) return m[1] + 'ft';
    var x = String(v.title || '').split(/\s*x\s*/);
    return x.length >= 3 ? parseInt(x[x.length - 1], 10) + 'ft' : 'post';
  }

  /* Gate variant titles are "Colour / SIZE / Frame", e.g.
       "Midnight Black (Most Popular) / 4' x 12' (Double Swings) / Pressure Treated (Ready to Install)".
       Owner 2026-08-31: "man gate and double swing need to be options". Width tells them apart —
       4ft wide is a man gate, 12ft wide is a double swing — so split on WIDTH, not on the
       marketing words, which are absent from several sizes ("5' x 4'" carries no label at all). */
  /* ⛔⛔ OPTION ORDER DIFFERS PER GATE PRODUCT. The profile gates read
       "Colour / Size / Frame" but the ZM gate reads "Size / Colour / Metal Style" — so indexing
       parts[1] read the ZM gate's COLOUR as its size, matched no height, and the row rendered with
       no options at all. Scan every segment for the size pattern instead of trusting a position. */
  function gateSize(v) {
    var parts = String(v.title || '').split(/\s*\/\s*/);
    for (var i = 0; i < parts.length; i++) {
      var m = parts[i].match(/(\d+)\s*'\s*x\s*(\d+)\s*'/);
      if (m) return { h: parseInt(m[1], 10), w: parseInt(m[2], 10) };
    }
    return null;
  }
  function gateKind(v) {
    var g = gateSize(v);
    if (!g) return null;
    return g.w >= 8 ? 'Double swing' : 'Man gate';
  }
  /* ⭐ How many hardware kits ONE gate of this kind needs. Owner 2026-09-08: a double swing is
     two leaves, two sets of hinges and a drop peg, so it takes TWO kits. Sizing hardware off the
     gate COUNT undercharged every double swing by a kit. */
  function kitsFor(kind) { return kind === 'Double swing' ? 2 : 1; }
  /* ⭐ POSTS PER GATE KIND (owner 2026-10-04). A double swing hangs between two posts; in a
     continuous run one side can be shared, but the owner chose 2 to be safe rather than
     under-ship a gate. A man gate stays at 1. Same shape as kitsFor so the two rules read
     together. */
  function postsFor(kind) { return kind === 'Double swing' ? 2 : 1; }
  function pendingGatePosts(el) {
    var p = 0;
    [].slice.call(el.querySelectorAll('[data-bb-gate-kind][aria-pressed="true"]'))
      .forEach(function (x) { p += postsFor(x.getAttribute('data-bb-gate-kind')); });
    return p;
  }
  /* the posts row is built once, from the cart — this keeps its label honest as gates are
     ticked in the bundle, which it never did before (it under-stated by one per gate). */
  function refreshPostLabel(el, state) {
    var lab = el.querySelector('[data-bb-post-lab]');
    if (!lab) return;
    var base = state.panels > 0 ? state.panels + 1 : 0;
    var need = base + state.gatePosts + pendingGatePosts(el) - state.posts;
    lab.textContent = need > 0
      ? 'You need ' + need + ' more post' + (need === 1 ? '' : 's')
      : 'A post for your gate';
  }

  /* cheapest available gate of each kind at this height, preferring the panel's own species */
  function gateChoices(product, h, species, colour) {
    if (!product || !product.variants) return [];
    var av = product.variants.filter(function (v) {
      var g = gateSize(v);
      return v.available && g && g.h === h;
    });
    if (!av.length) return [];
    /* ⛔⛔ EXACT MATCH WINS. The permissive rule below reads a MISSING value as "no constraint",
       which is right in general but let "Full Metal Frame" (species = '') and a Silver gate survive
       against a Black, cedar-framed run — and being cheapest, they won. Owner 2026-08-31: "big
       error i have black and its adding a silver gate with full metal frame".
       So: filter permissively, then if an EXACT match exists on that axis, keep only those. The
       permissive result stays as the fallback so an axis we cannot read never empties the list. */
    var bySp = av.filter(function (v) { var sp = speciesOf(v.title); return !species || !sp || sp === species; });
    if (!bySp.length) bySp = av;
    if (species) {
      var exactSp = bySp.filter(function (v) { return speciesOf(v.title) === species; });
      if (exactSp.length) bySp = exactSp;
    }
    /* ⛔ COLOUR IS ABSOLUTE. The old code had two escape hatches: an unmatched colour fell back to
       bySp (ANY colour), and an EMPTY colour skipped the block entirely — after which selection
       fell through to raw VARIANT ORDER. CA lists Silver first and USA lists Midnight Black first,
       which is exactly why a black run was offered a SILVER gate on Canada only. A gate in the
       wrong colour is worse than no gate, so an empty result is a legitimate answer here. */
    if (colour) {
      var exactC = bySp.filter(function (v) { return gateColour(v) === colour; });
      /* only a COLOURLESS variant may stand in — never a different colour */
      bySp = exactC.length ? exactC : bySp.filter(function (v) { return !gateColour(v); });
    } else {
      /* colour unknown: the owner's order, never the catalogue's. colourOf maps corten -> 'rust'. */
      var COL_ORDER = ['black', 'brown', 'silver', 'rust'];
      for (var ci = 0; ci < COL_ORDER.length; ci++) {
        var want = COL_ORDER[ci];
        var pickC = bySp.filter(function (v) { return gateColour(v) === want; });
        if (pickC.length) { bySp = pickC; break; }
      }
    }

    /* ⭐ FRAME, cheapest first (owner): treated -> cedar -> full metal frame. Only when the panel
       does not already dictate a species — otherwise the species filter above has decided. Full
       metal frame carries no species keyword, so it is what remains if neither wood option is
       stocked; it must never be the DEFAULT suggestion, being the most expensive. */
    if (!species) {
      var FRAME_ORDER = ['treated', 'cedar'];
      for (var fi = 0; fi < FRAME_ORDER.length; fi++) {
        var wantF = FRAME_ORDER[fi];
        var pickF = bySp.filter(function (v) { return speciesOf(v.title) === wantF; });
        if (pickF.length) { bySp = pickF; break; }
      }
    }

    var out = [];
    ['Man gate', 'Double swing'].forEach(function (kind) {
      var pick = bySp.filter(function (v) { return gateKind(v) === kind; })
        .sort(function (a, b) { return a.price - b.price; })[0];
      if (pick) out.push({ kind: kind, v: pick });
    });
    return out;
  }



  /* ── rendering ───────────────────────────────────────────────────────────────────────── */
  var CSS = '' +
    '.bb-cadd{position:relative;margin:10px 12px;padding:11px 12px;border:1px solid rgba(235,103,60,.35);' +
      'border-radius:10px;background:#fff8f5;font-size:13px;line-height:1.45}' +
    '.bb-cadd__x{position:absolute;top:5px;right:5px;width:26px;height:26px;border:0;padding:0;' +
      'background:none;color:#a89f99;font:400 20px/1 system-ui,sans-serif;cursor:pointer;border-radius:50%}' +
    '.bb-cadd__x:hover{background:rgba(0,0,0,.06);color:#57534e}' +
    /* ⛔ THE GLYPH IS 26x26 BUT THE TARGET MUST NOT BE. Owner 2026-10-04: the far right
       parts did not respond well to touch on mobile. 26px is about half what a thumb needs,
       and it sits 5px off the card edge. This extends the HIT AREA to 41x44, out to the card
       edge, without moving the glyph or reflowing the header, which reserves
       padding-right:26px for it. */
    '.bb-cadd__x::before{content:"";position:absolute;top:-9px;right:-5px;bottom:-9px;left:-10px}' +
    '.bb-cadd__x{touch-action:manipulation}' +
    '.bb-cadd__hd{padding-right:26px;font-size:14px;font-weight:800;color:#1c1917}' +
    '.bb-cadd__cnt{display:block;font-size:12px;font-weight:600;color:#8a7f78;margin-top:1px}' +
    '.bb-cadd__sec{display:flex;gap:9px;margin-top:9px;padding-top:9px;' +
      'border-top:1px solid rgba(235,103,60,.18)}' +
    '.bb-cadd__n{flex:none;width:19px;height:19px;margin-top:1px;border-radius:50%;' +
      'background:#EB673C;color:#fff;font:800 11px/19px system-ui,sans-serif;text-align:center}' +
    '.bb-cadd__body{flex:1;min-width:0}' +
    '.bb-cadd__foot{margin-top:10px;padding-top:9px;border-top:1px solid rgba(235,103,60,.18);' +
      'color:#8a7f78;font-size:12px;line-height:1.4}' +
    '.bb-cadd__val{color:#1c1917;font-weight:700}' +
    '.bb-cadd__secx{flex:none;align-self:flex-start;width:22px;height:22px;border:0;padding:0;' +
      'background:none;color:#c4bbb4;font:400 16px/1 system-ui,sans-serif;cursor:pointer;border-radius:50%}' +
    '.bb-cadd__secx:hover{background:rgba(0,0,0,.06);color:#57534e}' +
    '.bb-cadd__sec[data-min="1"] .bb-cadd__sub,' +
    '.bb-cadd__sec[data-min="1"] .bb-cadd__types,' +
    '.bb-cadd__sec[data-min="1"] .bb-cadd__pick{display:none!important}' +
    '.bb-cadd[data-min="1"] .bb-cadd__sec,' +
    '.bb-cadd[data-min="1"] .bb-cadd__foot{display:none!important}' +
    '.bb-cadd[data-min="1"] .bb-cadd__hd{padding-bottom:0}' +
    '.bb-cadd__line{display:flex;align-items:center;gap:10px;flex-wrap:wrap}' +
    '.bb-cadd__lab{font-weight:700;color:#1c1917}' +
    '.bb-cadd__sub{display:block;color:#57534e;font-size:12.5px;margin-top:2px}' +
    '.bb-cadd__types{display:inline-flex;gap:6px;margin-left:auto}' +
    '.bb-cadd__t{padding:6px 12px;border:1px solid #ddd5cf;border-radius:999px;background:#fff;' +
      'font:600 12.5px/1 inherit;color:#57534e;cursor:pointer;white-space:nowrap}' +
    '.bb-cadd__t[aria-pressed="true"]{background:#EB673C;border-color:#EB673C;color:#fff}' +
    /* BB-TILE-CSS — image tiles for the choice buttons. Pure CSS so the drawer's destroy-and-
       re-render cycle cannot strip it, and so no render-path code has to change. */
    '.bb-cadd__types{flex-wrap:wrap}' +
    '.bb-cadd__t[data-bb-type],.bb-cadd__t[data-bb-gate-kind]{' +
      'display:inline-flex;flex-direction:column;align-items:center;gap:5px;' +
      'padding:7px 10px 8px;border-radius:10px;min-width:74px;line-height:1.15}' +
    '.bb-cadd__t[data-bb-type]::before,.bb-cadd__t[data-bb-gate-kind]::before{' +
      'content:"";display:block;width:46px;height:46px;border-radius:7px;' +
      'background:#f3f1ef center/cover no-repeat;border:1px solid rgba(0,0,0,.06)}' +
    '.bb-cadd__t[data-bb-gate-kind]::before{background-image:var(--bb-ti)}' +
    /* ⭐ the picked COLOUR, in words. inherit the colour so it stays readable on the pressed
       (orange) state as well as the resting one. */
    '.bb-cadd__t[data-bb-gate-kind] .bb-cadd__tc{display:block;font-size:10.5px;font-weight:700;opacity:.72;letter-spacing:.01em}' +
    '/* BB-CONV-TILE */' +
    '.bb-cadd__t[data-bb-conv]{display:inline-flex;flex-direction:column;align-items:center;' +
    'gap:5px;padding:7px 10px 8px;border-radius:10px;min-width:74px;line-height:1.15}' +
    '.bb-cadd__t[data-bb-conv]::before{content:"";display:block;width:46px;height:46px;' +
    'border-radius:7px;background:#f3f1ef center/cover no-repeat;border:1px solid rgba(0,0,0,.06)}' +
    '.bb-cadd__t[data-bb-conv]::before{background-image:var(--bb-ti)}' +
    '/* BB-BUNDLE-ADD */' +
    '.bb-cadd__allrow{padding:10px 12px 12px}' +
    '.bb-cadd__all{width:100%}' +
    '.bb-cadd__linetot{display:block;font-weight:700;color:#1c1917;margin-top:3px}' +
    '.bb-cadd__optt{display:inline-block;margin-top:5px;padding:8px 13px;border:1px solid #ddd5cf;' +
    'border-radius:9px;background:#fff;color:#1c1917;font-weight:700;font-size:13.5px;cursor:pointer;' +
    'font-family:inherit;line-height:1.2}' +
    '.bb-cadd__optt:hover{border-color:#EB673C;color:#EB673C}' +
    '.bb-cadd__optt[aria-pressed="true"]{background:#EB673C;border-color:#EB673C;color:#fff}' +
    '.bb-cadd__optt[aria-pressed="true"]:hover{color:#fff}' +
    /* tick box — same 16px control as the tiles, placed LEFT because a pill is one line of text */
    '.bb-cadd__optt{position:relative;padding-left:34px}' +
    '.bb-cadd__optt::after{content:"";position:absolute;left:11px;top:50%;transform:translateY(-50%);width:16px;height:16px;border-radius:4px;border:1.5px solid #c9c2bb;background:#fff;box-sizing:border-box}' +
    '.bb-cadd__optt[aria-pressed="true"]::after{border-color:#fff;background:#fff url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27%3E%3Cpath d=%27M3.5 8.5l3 3 6-6%27 fill=%27none%27 stroke=%27%23EB673C%27 stroke-width=%272.4%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27/%3E%3C/svg%3E") center/13px no-repeat}' +
    '/* BB-TICKBOX */' +
    '.bb-cadd__t[data-bb-type],.bb-cadd__t[data-bb-gate-kind],.bb-cadd__t[data-bb-conv]{position:relative}' +
    '.bb-cadd__t[data-bb-type]::after,.bb-cadd__t[data-bb-gate-kind]::after,.bb-cadd__t[data-bb-conv]::after{content:"";position:absolute;top:6px;right:6px;width:16px;height:16px;border-radius:4px;border:1.5px solid #c9c2bb;background:#fff;box-sizing:border-box;transition:none}' +
    '.bb-cadd__t[aria-pressed="true"][data-bb-type]::after,.bb-cadd__t[aria-pressed="true"][data-bb-gate-kind]::after,.bb-cadd__t[aria-pressed="true"][data-bb-conv]::after{border-color:#fff;background:#fff url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27%3E%3Cpath d=%27M3.5 8.5l3 3 6-6%27 fill=%27none%27 stroke=%27%23EB673C%27 stroke-width=%272.4%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27/%3E%3C/svg%3E") center/13px no-repeat}' +
    '.bb-cadd__t[data-bb-type],.bb-cadd__t[data-bb-gate-kind],.bb-cadd__t[data-bb-conv]{padding-right:26px}' +
    
    '.bb-cadd__t[aria-pressed="true"][data-bb-conv]::before{outline:2px solid #fff;outline-offset:-2px}' +
    '@media(max-width:420px){.bb-cadd__t[data-bb-conv]{min-width:66px}' +
    '.bb-cadd__t[data-bb-conv]::before{width:40px;height:40px}}' +
    '.bb-cadd__t[data-bb-type="Metal"]::before{background-image:url(https://cdn.shopify.com/s/files/1/0727/8320/0557/files/Universal-Metal-Fence-Posts-for-Wood-Steel-and-Hybrid-Fences-BarrierBoss-42813555278125.jpg?width=120)}' +
    '.bb-cadd__t[data-bb-type="Wood"]::before{background-image:url(https://cdn.shopify.com/s/files/1/0727/8320/0557/files/Kiln-Dried-Pressure-Treated-Wood-for-Exterior-Use-BarrierBoss-38756736008493.webp?width=120)}' +
    /* pressed state must still read clearly against a photo */
    '.bb-cadd__t[aria-pressed="true"][data-bb-type]::before{' +
      'outline:2px solid #fff;outline-offset:-2px}' +
    '@media(max-width:420px){.bb-cadd__t[data-bb-type]{min-width:66px}' +
      '.bb-cadd__t[data-bb-type]::before{width:40px;height:40px}}' +
    '.bb-cadd__pick{margin-top:8px}' +
    '.bb-cadd__pick[hidden]{display:none!important}' +
    /* ⛔ .bb-cadd__sec{display:flex} is an AUTHOR rule and outranks the UA [hidden] rule, so
       sec.hidden = true was setting the attribute and changing nothing. Every standby caps/
       hardware row stayed on screen showing its build-time label with an empty body. */
    '.bb-cadd__sec[hidden]{display:none!important}' +
    /* ⛔ AND .bb-cadd__types, for the same reason — it carries display:inline-flex above, so the
       species row's own `hidden` attribute did nothing and Treated/Cedar showed under METAL.
       Owner 2026-09-14: "treated and cedar should only show if wood is selected". The JS was
       already correct; only this guard was missing. */
    '.bb-cadd__types[hidden]{display:none!important}' +
    '.bb-cadd__go{width:100%;margin-top:7px;padding:9px;border:0;border-radius:8px;' +
      'background:#EB673C;color:#fff;font-weight:800;cursor:pointer;font-size:13px}' +
    '.bb-cadd__go:disabled{background:#c9c2bd;cursor:default}' +
    '.bb-cadd__note{display:block;margin-top:4px;color:#8a7f78;font-size:12px}' +
    '.bb-cadd__free{color:#1B5E20;font-weight:700}';

  function injectCss() {
    if (document.getElementById('bb-cadd-css')) return;
    var s = document.createElement('style');
    s.id = 'bb-cadd-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function section(n, name, html) {
    var m = dismissedSecs().indexOf(name) > -1;
    return '<div class="bb-cadd__sec" data-bb-sec="' + name + '"' + (m ? ' data-min="1"' : '') + '>' +
           '<span class="bb-cadd__n">' + n + '</span>' +
           '<div class="bb-cadd__body">' + html + '</div>' +
           '<button type="button" class="bb-cadd__secx" data-bb-sec-min="' + name + '" ' +
             'aria-expanded="' + (m ? 'false' : 'true') + '" aria-label="Minimise this suggestion" ' +
             'title="' + (m ? 'Show' : 'Minimise') + '">' + (m ? '&plus;' : '&minus;') + '</button></div>';
  }





  function build(state) {
    var off = dismissedSecs();
    var live = function (name) { return off.indexOf(name) < 0; };
    /* ZM kits ship inline posts + ONE end post, but NOT gate posts (owner 2026-09-03,
       re-confirmed 09-09). A ZM kit therefore contributes 0 to the post BASE while its gates
       still demand one post each. The old formula multiplied out to 0 for a ZM-only cart,
       which is why the slide cart never offered the gate post the PDP had started offering. */
    var postBase = state.panels > 0 ? state.panels + 1 : 0;
    var need = postBase + state.gatePosts;   /* per KIND, not per gate */
    var zmOnly = (state.panels === 0 && state.zmPanels > 0);
    var shortPosts = (state.panels > 0 || (zmOnly && state.gates > 0)) ? need - state.posts : 0;
    var html = '';

    /* ⭐ GATES FIRST (owner 2026-08-31: "i would do gates before posts"). The gate count
       FEEDS the post BOM — one extra post per gate — so deciding the gate first makes the
       post number settle rather than jump after the shopper acts on row 2. */
    var n = 0;
    /* 1 · GATES — only when none in the cart AND a panel can actually take one.
       ⛔ maxGateH is the TALLEST panel in the cart. A cart of only 3ft panels gives 3, so the
          gate section is never built and the word "gate" never appears — owner 2026-08-31:
          "If its 3' tall stuff in cart then it shouldnt even mention gates". There is no 3ft
          gate in the catalogue on either store.
       ⭐ A MIXED cart (3ft + 6ft) still offers a gate, correctly — the 6ft run can take one.
       Unparsable heights leave maxGateH at 0, so this fails CLOSED. */
    var kindsLeft = ['Man gate', 'Double swing'].filter(function (k) { return !state.gateKinds[k]; });
    var gateBuilt = (kindsLeft.length && state.maxGateH >= MIN_GATE_H && state.hasAsm);
    if (gateBuilt) {
      html += section(++n, 'gate',
        '<div class="bb-cadd__line"><span class="bb-cadd__lab">' +
          (state.gates ? 'Add another gate' : 'Add a gate') + '</span>' +
        '<span class="bb-cadd__types" data-bb-gate-types></span></div>' +
        '<span class="bb-cadd__sub" data-bb-gate-sub></span>' +
        '<div class="bb-cadd__pick" data-bb-pick-gate hidden></div>');
    }

    /* 1b · HARDWARE — follows the GATE, the same pairing the PDP uses. Only for gates ALREADY in
       the cart: a gate the shopper has not bought yet has nothing to fit. */
    var shortHw = state.gateKits - state.hardware;
    /* ⭐ STANDBY when a gate is merely on offer. A section can only be created during build(), so
       it is created empty and renderHw() hides it until a gate is owned OR selected. The label is
       written at render time — at build time the standby count is 0. */
    if (shortHw > 0 || gateBuilt) {
      html += section(++n, 'hw',
        '<div class="bb-cadd__line"><span class="bb-cadd__lab" data-bb-hw-lab>Gate hardware</span></div>' +
        '<div class="bb-cadd__pick" data-bb-pick-hw hidden></div>');
    }

    /* 2 · POSTS — ZM panels deliberately excluded, they ship with theirs */
    var postsBuilt = (shortPosts > 0 && state.hasAsm);
    /* STANDBY, exactly as hardware does it: a section can only be created during build(), so
       for a ZM-only cart it is created empty and revealed the moment a gate is owned or PICKED.
       A gate post is METAL, so the type chips are omitted in that case - matching the PDP
       bundle, which suppresses the wood row on a ZM kit for the same reason. */
    var zmGatePost = (zmOnly && gateBuilt && state.hasAsm);
    if (postsBuilt || zmGatePost) {
      html += section(++n, 'post',
        '<div class="bb-cadd__line"><span class="bb-cadd__lab" data-bb-post-lab>' +
          (shortPosts > 0
            ? 'You need ' + shortPosts + ' more post' + (shortPosts === 1 ? '' : 's')
            : 'A post for your gate') + '</span>' +
        (zmGatePost && shortPosts <= 0 ? '' :
        '<span class="bb-cadd__types">' +
          '<button type="button" class="bb-cadd__t" data-bb-type="Metal">Metal</button>' +
          '<button type="button" class="bb-cadd__t" data-bb-type="Wood">Wood</button>' +
        '</span>' +
        /* ⭐ SPECIES, revealed only once Wood is picked. Owner 2026-09-11: cedar was unreachable
           from the cart — the row inherited the run's species and offered no way to change it. */
        '<span class="bb-cadd__types" data-bb-post-sp hidden>' +
          '<button type="button" class="bb-cadd__t" data-bb-sp="treated">Treated</button>' +
          '<button type="button" class="bb-cadd__t" data-bb-sp="cedar">Cedar</button>' +
        '</span>') + '</div>' +
        /* ⛔ ONE rule line, ONE price line. The earlier version repeated "sized to your tallest
           panel" in both the summary and the picked-variant note, and printed the full variant
           title — three lines saying the same thing. Owner: "WAY TO MUCH text". */
        '<span class="bb-cadd__sub" data-bb-post-from></span>' +
        '<div class="bb-cadd__pick" data-bb-pick-post hidden></div>');
    }

    /* 2b · CAPS — follow WOOD posts only. Steel posts ship with caps, so a metal cart never sees
       this row. Owner: "steel posts already come with caps". */
    var shortCaps = state.woodPosts - state.caps;
    /* standby for the same reason as hardware — revealed the moment Wood is picked */
    if (shortCaps > 0 || postsBuilt) {
      html += section(++n, 'cap',
        '<div class="bb-cadd__line"><span class="bb-cadd__lab" data-bb-cap-lab>Post caps</span></div>' +
        '<div class="bb-cadd__pick" data-bb-pick-cap hidden></div>');
    }

    /* 2c · POST SAVERS — CEDAR only (owner 2026-09-14: "if I select cedar posts in slide bundle it
       should pre select post savers"). Standby exactly like caps: built here because a section can
       only be created during build(), then revealed and pre-selected the moment Cedar is pressed. */
    if (postsBuilt) {
      html += section(++n, 'saver',
        '<div class="bb-cadd__line"><span class="bb-cadd__lab" data-bb-saver-lab>Post savers</span></div>' +
        '<div class="bb-cadd__pick" data-bb-pick-saver hidden></div>');
    }

    /* 3 · CONVERT — the lane moves with it, so the figure quoted is ALL-IN */
    /* ⭐ LEAD WITH THE HOOK, not a neutral label (owner 2026-08-31: "you have to hook people
       somehow so say save x to convert to loose or save on labour or something for framed").
       The headline is primed asynchronously with the REAL number as soon as the swap is priced —
       see primeConvert(). Until then it carries the benefit, never a fake figure.
       ⛔ No invented labour hours. "Arrives ready to install" is the true benefit; the only
          numbers shown are ones actually computed from the live catalogue. */
    var conv = '';
    if (state.loose.length) {
      var lq = state.loose.reduce(function (a, l) { return a + l.qty; }, 0);
      conv = section(n + 1, 'conv',
        '<div class="bb-cadd__line"><span class="bb-cadd__lab" data-bb-conv-lab>' +
          'We frame your ' + lq + ' panel' + (lq === 1 ? '' : 's') + '</span>' +
        '<span class="bb-cadd__types">' +
          '<button type="button" class="bb-cadd__t" data-bb-conv="treated">Treated</button>' +
          '<button type="button" class="bb-cadd__t" data-bb-conv="cedar">Cedar</button>' +
        '</span></div>' +
        '<span class="bb-cadd__sub" data-bb-conv-sub></span>' +
        '<div class="bb-cadd__pick" data-bb-pick-conv hidden></div>');
    }
    /* ⛔ NO DOWNSELL. A framed cart used to be offered "Save by going loose" here — a switch
       DOWN on a decision the shopper has already made, which read as confusing and works
       against the point of this block (owner 2026-10-04: "we're trying to max their cart").
       Loose carts still get the framing upsell above; framed carts keep the rest of the
       upsell — posts, gates, caps — and are simply never shown the way down. */
    html += conv;

    if (!html) return null;

    var wrap = document.createElement('div');
    wrap.className = 'bb-cadd';
    wrap.setAttribute(FLAG, '1');
    wrap.innerHTML =
      '<button type="button" class="bb-cadd__x" data-bb-min aria-expanded="true" ' +
        'aria-label="Minimise" title="Minimise">&minus;</button>' +
      /* ⛔ NOT "Before you check out" (owner 2026-08-31) — it reads as checkout pressure at the
         exact moment the shopper is deciding. This block is an order check, not a nag, so it is
         named after the job and states how many items there are to weigh up. */
      '<div class="bb-cadd__hd">Ready to Install' +
        /* ⛔ Owner 2026-10-30: the saving pill was removed here together with the discount behind
           it. Built as a concatenated string, so there is no CSS hook — it is simply not emitted. */
        '</div>' +
      html +
      /* ⛔ BB-MINORDER-FIXED — THE OLD RATIONALE HERE RETIRED WITH THE MINIMUM. This note used to offer, as
         a "literally true" fact, that a small top-up order could not be shipped at all. The
         four-band card has no minimum, so that is no longer true and must never be quoted back to
         a shopper as a reason to decide now.
         ⭐ The row needs no urgency argument: it is the bundle action for whatever is ticked
         above. Corrected 2026-10-06. */
      '<div class="bb-cadd__allrow">' +
        '<button type="button" class="bb-cadd__go bb-cadd__all" data-bb-addall disabled>Nothing selected</button>' +
      '</div>';

    /* ⭐ STEP NUMBERS FOLLOW WHAT IS VISIBLE (owner 2026-09-14: the panel read 1, 3, 4, 5).
       section() stamps its number at BUILD time, but hardware, caps and the gate post are built as
       STANDBY and hidden until they matter — so a hidden section consumed a number and the visible
       ones skipped.
       ⛔ AN OBSERVER, NOT A CALL AT EACH SITE: sec.hidden is toggled from eight places across four
          render functions and their async bail-outs. Adding renumber() to each guarantees the ninth
          is missed, which is how this drifted to begin with.
       ⛔ It filters on the `hidden` ATTRIBUTE while renumber writes textContent, so it cannot
          re-enter itself. A childList observer here WOULD loop. */
    function bbRenumber(root) {
      var i = 0;
      [].slice.call(root.querySelectorAll('.bb-cadd__sec')).forEach(function (s) {
        if (s.hidden) return;
        var b = s.querySelector('.bb-cadd__n');
        if (!b) return;
        i++;
        if (b.textContent !== String(i)) b.textContent = i;
      });
    }
    bbRenumber(wrap);
    if (window.MutationObserver) {
      new MutationObserver(function () { bbRenumber(wrap); }).observe(wrap, {
        attributes: true, subtree: true, attributeFilter: ['hidden']
      });
    }
    /* ⛔ NO delivery / crane / freight messaging in this card (owner 2026-08-31: "ignore this
       delivery and crane messaging — its in the PDP above"). The PDP owns the delivery story;
       repeating it here duplicated it. This section speaks only to PRODUCT price and what is
       missing from the run.
       ⚠️ BB-MINORDER-FIXED — the old footer also cited an order minimum, and the note here said the
       card was "already suppressed" below it. Both are now void: the four-band card has no
       minimum and the subtotal gate was removed. Corrected 2026-10-06. */
    return { el: wrap, shortPosts: shortPosts };
  }

  /* ⭐ STAMP THE SOURCE. Without it an order containing posts is indistinguishable from one where
     the shopper found posts through the menu — which they have always done at 8-13% — so attach
     rate can never answer "did this feature work". The underscore prefix keeps the property OFF
     the customer's order confirmation while it persists on the order for reporting.
     ⛔ Orders placed before this shipped are unattributable FOREVER; that is why it went in early. */
  function tag(items, src) {
    return items.map(function (i) {
      var props = { _bb_src: src };
      /* ⭐ per-line properties (e.g. the gate's "Wire Opening") ride along beside the source stamp */
      if (i.props) { for (var k in i.props) { if (i.props[k]) props[k] = i.props[k]; } }
      return { id: i.id, quantity: i.quantity, properties: props };
    });
  }

  /* ⭐ ONE BUNDLE ADD. Each section records what it WOULD add; the footer posts them together.
     ⛔ Convert is excluded on purpose — it is a swap, not an add (see the file header). */
  function setItem(el, pick, item) {
    var sec = pick.closest('[data-bb-sec]');
    /* __bbItemSaved survives an un-tick so re-ticking restores the exact same line — no second
       product fetch, and no chance of re-deriving a price that drifted mid-session. */
    if (sec) { sec.__bbItemSaved = item; sec.__bbItem = item; }
    refreshAll(el);
  }
  function clearItem(el, sec) { if (sec) sec.__bbItem = null; refreshAll(el); }
  /* ⭐ a section may hold ONE item or an ARRAY of them. Gates need the array: a run can
     take a man gate AND a double swing, and each is its own line. Everything else still
     stores a single object, so this normalises rather than migrates. */
  function itemsOf(s) {
    var it = s && s.__bbItem;
    if (!it) return [];
    return (Object.prototype.toString.call(it) === '[object Array]') ? it : [it];
  }
  function bundleItems(el) {
    var out = [];
    [].slice.call(el.querySelectorAll('[data-bb-sec]')).forEach(function (s) {
      /* ⛔ skip HIDDEN sections — a standby row that recorded an item and then hid again when the
         chip was cleared would otherwise still be charged for. */
      if (s.hidden) return;
      /* ⛔ props must survive to the POST — dropping them here is what left slide-cart gates with
         no Wire Opening while PDP gates carried one. */
      itemsOf(s).forEach(function (it) {
        if (it && it.id) out.push({ id: it.id, quantity: it.quantity, props: it.props });
      });
    });
    return out;
  }
  function bundleTotal(el) {
    var sum = 0;
    [].slice.call(el.querySelectorAll('[data-bb-sec]')).forEach(function (s) {
      if (s.hidden) return;
      itemsOf(s).forEach(function (it) { if (it && it.id) sum += (it.price || 0); });
    });
    return sum;
  }
  function refreshAll(el) {
    var btn = el.querySelector('[data-bb-addall]');
    if (!btn) return;
    var row = btn.closest('.bb-cadd__allrow') || btn.parentNode;
    var items = bundleItems(el);
    btn.disabled = !items.length;
    /* ⭐ The button FOLLOWS THE SELECTION (owner 2026-09-03): it sits directly under the last
       section the shopper has ticked, so "gate + hardware" puts it under hardware instead of at
       the foot of the card past rows they chose not to take. Hidden outright when nothing is
       ticked — a permanently disabled CTA is just noise. */
    if (row) row.hidden = !items.length;
    if (!items.length) return;
    btn.textContent = 'Add ' + items.length + ' item' + (items.length === 1 ? '' : 's') +
      ' — ' + money(bundleTotal(el));
    var last = null;
    [].slice.call(el.querySelectorAll('[data-bb-sec]')).forEach(function (s) {
      /* ⛔ the last section carrying an ITEM, not the last VISIBLE one — they differ whenever a
         standby caps/hardware row is on screen but unticked. */
      if (!s.hidden && itemsOf(s).length) last = s;
    });
    /* ⛔ only move when it is not already there: reparenting on every refresh drops focus and
       retriggers layout while the shopper is still ticking. */
    if (row && last && last.parentNode && last.nextSibling !== row) {
      last.parentNode.insertBefore(row, last.nextSibling);
    }
  }

  /* ⛔⛔ /cart/add.js with an items[] ARRAY IS ALL-OR-NOTHING. The gate bundle posts the gate and
     the hardware kit together, so ONE rejected line lost the whole bundle and the shopper saw a
     bare "Could not add" — the gate they actually wanted never made it. Reproduced live: gate +
     hardware kit returned 422 "already sold out" for the KIT while each line added fine alone.
     A bundle must never be more fragile than its parts. Owner 2026-09-03. */
  function addPost(items) {
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return true;
    });
  }
  function cartAdded() {
    if (window.theme && theme.Cart && theme.Cart.prototype &&
        typeof theme.Cart.prototype._onProductAdded === 'function') {
      theme.Cart.prototype._onProductAdded();
    }
  }
  function addToCart(items, btn, label) {
    btn.disabled = true; btn.textContent = 'Adding…';
    items = tag(items, 'cart-addons');
    return addPost(items).then(function () { cartAdded(); }).catch(function () {
      /* ⭐ Atomic add failed. Retry LINE BY LINE and keep whatever succeeds, so an add-on that is
         out of stock cannot block the main item. Deterministic — no parsing of Shopify's message,
         which is display text and localised. ONE pass, never a loop. */
      if (!items || items.length < 2) {
        btn.disabled = false;
        btn.textContent = label ? 'Could not add — try again' : 'Could not add';
        return;
      }
      var okN = 0;
      return items.reduce(function (chain, it) {
        return chain.then(function () {
          return addPost([it]).then(function () { okN++; })['catch'](function () {});
        });
      }, Promise.resolve()).then(function () {
        if (!okN) {
          /* ⛔ never "sold out" — the shopper cannot tell our bug from real stock */
          btn.disabled = false;
          btn.textContent = label ? 'Could not add — try again' : 'Could not add';
          return;
        }
        /* ⭐ say what actually happened rather than claiming total failure */
        if (okN < items.length) btn.textContent = 'Added ' + okN + ' of ' + items.length;
        cartAdded();
      });
    });
  }

  function wire(el, state) {
    el.addEventListener('click', function (e) {
      /* ⭐ MINIMISE, never remove (owner 2026-08-31: "i wouldnt do full closes i would do
         minimises"). Nothing is destroyed — the header stays as the handle to bring it back, so a
         shopper who collapsed it out of the way can always reopen it. */
      var all = e.target.closest('[data-bb-addall]');
      if (all) {
        e.preventDefault(); e.stopPropagation();
        var bundle = bundleItems(el);
        if (!bundle.length) return;
        addToCart(bundle, all);
        return;
      }

      var cx = e.target.closest('[data-bb-min]');
      if (cx) {
        e.preventDefault(); e.stopPropagation();
        var min = el.getAttribute('data-min') !== '1';
        el.setAttribute('data-min', min ? '1' : '0');
        cx.setAttribute('aria-expanded', min ? 'false' : 'true');
        cx.innerHTML = min ? '&plus;' : '&minus;';
        cx.setAttribute('title', min ? 'Show' : 'Minimise');
        setDismissed(min);
        return;
      }

      /* ⭐ COLLAPSED: a tap anywhere on the card opens it (owner 2026-10-03: the plus
         "doesn't open, I have to click in like left"). The toggle is 26x26 pinned to the
         top-right corner, which is a hard target on a phone and sits right on the card
         edge. This runs AFTER the [data-bb-min] branch, so the plus itself is unchanged,
         and it ignores taps on any real control so the chips and Add buttons still work. */
      if (el.getAttribute('data-min') === '1' &&
          !e.target.closest('button,a,input,select,textarea,label,[data-bb-addall]')) {
        e.preventDefault();
        el.setAttribute('data-min', '0');
        var px = el.querySelector('[data-bb-min]');
        if (px) {
          px.setAttribute('aria-expanded', 'true');
          px.innerHTML = '&minus;';
          px.setAttribute('title', 'Minimise');
        }
        setDismissed(false);
        return;
      }

      /* posts */
      var sx = e.target.closest('[data-bb-sec-min]');
      if (sx) {
        e.preventDefault(); e.stopPropagation();
        var sec = sx.closest('[data-bb-sec]');
        if (!sec) return;
        var smin = sec.getAttribute('data-min') !== '1';
        sec.setAttribute('data-min', smin ? '1' : '0');
        sx.setAttribute('aria-expanded', smin ? 'false' : 'true');
        sx.innerHTML = smin ? '&plus;' : '&minus;';
        sx.setAttribute('title', smin ? 'Show' : 'Minimise');
        if (smin) dismissSec(sx.getAttribute('data-bb-sec-min')); else undismissSec(sx.getAttribute('data-bb-sec-min'));
        return;
      }

      /* BB-CHIP-UNTOGGLE — clicking the chip that is already selected clears it.
         Without this the selection is a one-way door: you can move Metal -> Wood but never back to
         none, and the section keeps a committed-looking Add CTA the shopper never asked for.
         Runs BEFORE the per-family branches below, so selecting behaves exactly as it always did. */
      /* ⛔ BEFORE the chip-untoggle branch below: that branch wipes .bb-cadd__pick innerHTML, which
         would destroy the very button being clicked here. */
      var opt = e.target.closest('[data-bb-opt]');
      if (opt) {
        e.preventDefault(); e.stopPropagation();
        var optOn = opt.getAttribute('aria-pressed') !== 'true';
        opt.setAttribute('aria-pressed', optOn ? 'true' : 'false');
        var osec = opt.closest('[data-bb-sec]');
        if (osec) osec.__bbItem = optOn ? (osec.__bbItemSaved || null) : null;
        refreshAll(el);
        return;
      }

      /* ⛔ gate chips are NOT in this generic un-press branch any more: it clears the whole
         section, which would drop the other gate kind too. The gate handler below toggles
         one chip and recomputes from whatever is still pressed. */
      var pressedChip = e.target.closest('[data-bb-type],[data-bb-conv]');
      if (pressedChip && pressedChip.getAttribute('aria-pressed') === 'true') {
        e.preventDefault();
        pressedChip.setAttribute('aria-pressed', 'false');
        var chipSec = pressedChip.closest('.bb-cadd__sec') || el;
        var chipPick = chipSec.querySelector('.bb-cadd__pick');
        if (chipPick) { chipPick.hidden = true; chipPick.innerHTML = ''; }
        /* the dependant row was showing a count that included the pending add — put it back */
        clearItem(el, chipSec);
        if (pressedChip.hasAttribute('data-bb-type')) { primeCaps(el, state); primeSavers(el, state); }
        if (pressedChip.hasAttribute('data-bb-gate-kind')) primeHw(el, state);
        return;
      }

      var t = e.target.closest('[data-bb-type]');
      if (t) {
        e.preventDefault();
        var type = t.getAttribute('data-bb-type');
        [].slice.call(el.querySelectorAll('[data-bb-type]')).forEach(function (x) {
          x.setAttribute('aria-pressed', x === t ? 'true' : 'false');
        });
        /* ⭐ Wood reveals the species chips, pre-set to the RUN's own species so the default is
           the matching one; Metal puts them away. Owner 2026-09-11. */
        var spRow = el.querySelector('[data-bb-post-sp]');
        var sp = null;
        if (type === 'Wood') {
          sp = (state.species === 'cedar') ? 'cedar' : 'treated';
          if (spRow) {
            spRow.hidden = false;
            [].slice.call(spRow.querySelectorAll('[data-bb-sp]')).forEach(function (x) {
              x.setAttribute('aria-pressed', x.getAttribute('data-bb-sp') === sp ? 'true' : 'false');
            });
          }
        } else if (spRow) {
          spRow.hidden = true;
          [].slice.call(spRow.querySelectorAll('[data-bb-sp]')).forEach(function (x) {
            x.setAttribute('aria-pressed', 'false');
          });
        }
        renderPostPick(el, state, type, sp);
        return;
      }

      /* species chips — Wood only, and they cannot be un-picked (see header) */
      var spb = e.target.closest('[data-bb-sp]');
      if (spb) {
        e.preventDefault();
        var chosen = spb.getAttribute('data-bb-sp');
        [].slice.call(el.querySelectorAll('[data-bb-sp]')).forEach(function (x) {
          x.setAttribute('aria-pressed', x === spb ? 'true' : 'false');
        });
        renderPostPick(el, state, 'Wood', chosen);
        return;
      }

      /* gates */
      var gk = e.target.closest('[data-bb-gate-kind]');
      if (gk) {
        e.preventDefault();
        /* ⭐ MULTI-SELECT (owner 2026-10-04). A run often needs BOTH a man gate and a double
           swing, which the radio behaviour here made impossible. Toggle only the chip that
           was pressed and recompute from every chip still pressed. */
        gk.setAttribute('aria-pressed', gk.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        var gp = el.querySelector('[data-bb-pick-gate]');
        var info = el.__gate;
        if (!info) return;
        var gsec = gp.closest('[data-bb-sec]');
        var picked = [].slice.call(el.querySelectorAll('[data-bb-gate-kind][aria-pressed="true"]'))
          .map(function (x) { return x.getAttribute('data-bb-gate-kind'); });
        var chosen = picked.map(function (k) {
          return info.choices.filter(function (x) { return x.kind === k; })[0];
        }).filter(Boolean);
        if (!chosen.length) {
          /* last chip un-ticked — put the dependants back to what the CART alone needs */
          gp.hidden = true; gp.innerHTML = '';
          clearItem(el, gsec);
          renderHw(el, state, state.gateKits - state.hardware, state.gates);
          if (state.panels === 0 && state.zmPanels > 0) {
            renderGatePost(el, state, state.gatePosts - state.posts);
          }
          refreshPostLabel(el, state);
          return;
        }
        /* ⭐ hardware is summed across the KINDS picked — a double swing takes two kits, a man
           gate one, so both together need three. A flat +1 per gate undercharged the swing. */
        var kits = chosen.reduce(function (a, c) { return a + kitsFor(c.kind); }, 0);
        renderHw(el, state, state.gateKits + kits - state.hardware, state.gates + chosen.length);
        var gposts = chosen.reduce(function (a, c) { return a + postsFor(c.kind); }, 0);
        if (state.panels === 0 && state.zmPanels > 0) {
          renderGatePost(el, state, state.gatePosts + gposts - state.posts);
        }
        refreshPostLabel(el, state);
        gp.hidden = false;
        gp.innerHTML = chosen.map(function (c) {
          return '<span class="bb-cadd__linetot">' + c.kind + ' &mdash; ' + money(c.v.price) + '</span>';
        }).join('');
        setItem(el, gp, chosen.map(function (c) {
          return { id: c.v.id, quantity: 1, price: c.v.price,
                   props: state.wire ? { 'Wire Opening': state.wire } : null };
        }));
        return;
      }
      if (false) {
        /* ⭐ Hardware follows the gate chip — the kits THIS kind needs, so a double swing
           brings two. A flat +1 here was the reported bug. */
        renderHw(el, state, state.gateKits + kitsFor(kind) - state.hardware, state.gates + 1);
        /* And so does the GATE POST on a ZM-only cart - the kit ships its own run posts but
           not the gate's. Same owner rule the PDP bundle follows. */
        if (state.panels === 0 && state.zmPanels > 0) {
          renderGatePost(el, state, state.gates + 1 - state.posts);
        }
        gp.hidden = false;
        gp.innerHTML = '<span class="bb-cadd__linetot">' + kind + ' — ' + money(c.v.price) + '</span>';
        /* ⭐ "we match your mesh" has to be written on the order — the gate has no opening axis. */
        setItem(el, gp, { id: c.v.id, quantity: 1, price: c.v.price,
                          props: state.wire ? { 'Wire Opening': state.wire } : null });
        return;
      }

      /* convert */
      var c = e.target.closest('[data-bb-conv]');
      if (c) {
        e.preventDefault();
        var want = c.getAttribute('data-bb-conv');
        [].slice.call(el.querySelectorAll('[data-bb-conv]')).forEach(function (x) {
          x.setAttribute('aria-pressed', x === c ? 'true' : 'false');
        });
        var cp = el.querySelector('[data-bb-pick-conv]');
        cp.hidden = false;
        cp.innerHTML = '<span class="bb-cadd__note">Pricing the swap…</span>';
        planConvert(state, want).then(function (plan) {
          if (!plan || !plan.items.length) {
            cp.innerHTML = '<span class="bb-cadd__note">No matching option for these panels.</span>';
            return;
          }
          /* ⛔ NO delivery line here. primeConvert() already writes the lane change into the
             section's sub the moment the card renders, so repeating it on pick printed
             "Residential Crane Delivery becomes FREE" TWICE, once with the "(was $49.00)" tail.
             ONE surface owns each fact: the sub owns the delivery change, the button owns the
             price. */
          paintConvert(el, plan, want);      /* keep the headline in step with the chip */
          var net = plan.delta;              /* product only; delivery is the PDP's story */
          /* ⛔ BB-MINORDER-FIXED — DO NOT OFFER THE DOWNGRADE ON A SMALL CART. "Switch and save — go loose"
             shrinks an order that is already small, against the point of this block (owner
             2026-10-04: "we're trying to max their cart"). The upgrade direction and the
             posts/gates prompts are unaffected — those grow it.
             ⚠️ THE ORIGINAL REASON WAS A FLOOR THAT NO LONGER EXISTS: this used to say the cart
             could not check out below the figure. The four-band card has no minimum, so the
             behaviour now rests on the cart-size argument above. Corrected 2026-10-06. */
          if (net < 0 && state.subtotal < MIN_ORDER) {
            cp.innerHTML = '<span class="bb-cadd__note">Framed panels are the upgrade here.</span>';
            return;
          }
          cp.innerHTML = '<button type="button" class="bb-cadd__go" data-go>' +
            (net >= 0 ? 'Add framing — ' + money0(net) : 'Switch and save ' + money0(-net)) + '</button>';
          cp.querySelector('[data-go]').addEventListener('click', function (ev) {
            doConvert(plan, ev.target);
          });
        });
      }
    });
  }

  /* ⛔ THE HEADLINE MUST FOLLOW THE CHOSEN SPECIES. primeConvert() painted it once for the
     DEFAULT ('treated') and the chip handler only rewrote the BUTTON, so toggling Treated <-> Cedar
     left the headline showing the treated price against a cedar selection — the two disagreed by
     $25 a panel. One painter, called from BOTH paths. */
  function paintConvert(el, plan, want) {
    var lab = el.querySelector('[data-bb-conv-lab]');
    var sub = el.querySelector('[data-bb-conv-sub]');
    if (!lab || !plan) return;
    var net = plan.delta;                  /* product only; delivery is the PDP's story */

    if (want === 'unframed') {
      if (net < 0) lab.textContent = 'Save ' + money0(-net) + ' — go loose';
      if (sub) {
        /* ⛔ "post/gate" was accurate until caps and hardware joined the strip. A cart may never
           quietly lose a line, and a note that misnames what it removed fails the same test. */
        sub.textContent = (plan.stripped && plan.stripped.count)
          ? 'removes ' + plan.stripped.count + ' add-on item' +
            (plan.stripped.count === 1 ? '' : 's')
          : '';
      }
    } else {
      lab.textContent = net >= 0
        ? 'We frame them for you — add ' + money0(net)
        : 'We frame them for you — ' + money0(-net) + ' less';
      /* Owner 2026-08-31: drop the per-panel figure; keep the on-site comparison; and say WHY
         our framing is better than site-built — purpose-built tooling, fasteners and technique.
         ⛔ No welding claim: the frames are treated timber and cedar. */
      if (sub) sub.textContent = 'Built with tooling, fasteners and techniques we developed for ' +
        'these frames. Ready to install.';
    }
  }

  /* Work out the counterpart variant for every convertible line. Frame Type is an option on the
     SAME product, so this is a variant swap — no cross-product mapping table is needed. */
  function planConvert(state, want) {
    var lines = want === 'unframed' ? state.framed : state.loose;
    if (!lines.length) return Promise.resolve(null);

    return Promise.all(lines.map(function (l) { return getProduct(l.handle); })).then(function (prods) {
      var items = [], removes = {}, delta = 0, anyFramedAfter = (want !== 'unframed');

      /* ⛔ Going LOOSE strips the posts and the gates too — owner 2026-08-31: "if i switch to a
         loose product it needs to remove posts and gates". They were chosen to match a framed
         run (species matched to the frame, gate matched to the panel), so leaving them behind
         would ship a matched gate against material that no longer exists. Their value is credited
         into the saving so the figure on the button is the REAL one, and the note says plainly
         what is being removed — a cart must never quietly lose a line the shopper added. */
      var stripped = { count: 0, value: 0 };
      if (want === 'unframed') {
        /* ⭐ caps follow the POSTS and hardware follows the GATES — both are leaving, so both
           of these are now fitting nothing. Dependent parts, not preferences. */
        state.postLines.concat(state.gateLines, state.capLines || [], state.hwLines || [])
          .forEach(function (l) {
          removes[l.id] = 0;
          stripped.count += l.qty;
          stripped.value += l.price * l.qty;
        });
      }

      lines.forEach(function (l, idx) {
        var p = prods[idx];
        if (!p || !p.variants) return;
        var cur = p.variants.filter(function (v) { return v.id === l.id; })[0];
        if (!cur) return;
        var fi = -1;
        (p.options || []).forEach(function (o, i) {
          var n = (typeof o === 'string') ? o : o.name;
          if (/frame/i.test(n)) fi = i;
        });
        if (fi < 0) return;

        var target = p.variants.filter(function (v) {
          if (!v.available || v.id === cur.id) return false;
          for (var i = 0; i < cur.options.length; i++) {
            if (i === fi) continue;
            if (v.options[i] !== cur.options[i]) return false;   /* hold size + opening */
          }
          var fv = String(v.options[fi] || '').toLowerCase();
          if (want === 'unframed') return fv.indexOf('unframed') > -1;
          if (want === 'cedar') return fv.indexOf('cedar') > -1;
          return fv.indexOf('treated') > -1;
        })[0];
        if (!target) return;

        items.push({ id: target.id, quantity: l.qty });
        removes[l.id] = 0;
        delta += (target.price - cur.price) * l.qty;
      });

      delta -= stripped.value;               /* removing them is part of the saving */
      /* null = zone unknown. Keep it null rather than coercing to 0: a missing rate must not be
         arithmetic'd into a "$0 delivery" claim. */
      return { items: items, removes: removes, delta: delta, stripped: stripped };
    });
  }

  /* add the replacements, then zero the originals. Two calls, in that order, so a failure on the
     second leaves the shopper with MORE than they had rather than an emptied cart. */
  function doConvert(plan, btn) {
    btn.disabled = true; btn.textContent = 'Swapping…';
    /* separate source so an upgrade/downgrade is countable apart from a posts/gate add */
    var convItems = tag(plan.items, 'cart-convert');
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: convItems })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: plan.removes })
      });
    }).then(function () {
      if (window.theme && theme.Cart && theme.Cart.prototype &&
          typeof theme.Cart.prototype._onProductAdded === 'function') {
        theme.Cart.prototype._onProductAdded();
      }
    }).catch(function () {
      btn.disabled = false;
      btn.textContent = 'Could not swap — try again';
    });
  }

  function mount(state) {
    /* ⛔ THE DRAWER IS ITSELF A <form action="/cart"> CONTAINING THE ITEM LIST, so a generic
       "cart page" selector matches INSIDE #sidebar-cart. With the drawer target and the page
       target then nested, the per-target idempotence check passed on both and the card rendered
       TWICE in the same drawer. Exclude anything inside #sidebar-cart from the page lookup, and
       drop any target that contains (or sits inside) another. */
    var targets = [];
    var drawerMain = document.querySelector('#sidebar-cart .Drawer__Main');
    if (drawerMain) targets.push(drawerMain);

    var pageCart = null;
    var pageCands = document.querySelectorAll('.CartPage__Wrapper, .cart__items, .Cart__ItemList');
    for (var i = 0; i < pageCands.length; i++) {
      if (!pageCands[i].closest('#sidebar-cart')) { pageCart = pageCands[i]; break; }
    }
    if (pageCart) targets.push(pageCart);

    targets = targets.filter(function (t, i) {
      return !targets.some(function (o, j) { return j !== i && (o.contains(t) || t.contains(o)) && j < i; });
    });

    targets.forEach(function (t) {
      /* global guard as well as per-target: one card per cart surface, never a stack */
      if (t.querySelector('[' + FLAG + ']')) return;
      if (t.closest && t.closest('#sidebar-cart') && document.querySelector('#sidebar-cart [' + FLAG + ']')) return;
      var built = build(state);
      if (!built) return;
      /* ⭐ Sit directly under the cart lines, ABOVE "Share My Cart" — appending put it at the very
         bottom, stranded under Order notes where nobody scrolls (owner 2026-09-01: "should be above
         share cart .. its stuck under order notes"). Anchor on the first of the blocks that follow
         the items; fall back to appending only if none is present. */
      var after = t.querySelector('.bb-share-cart, [class*="share-cart"], .bb-bucks, [class*="bucks"], ' +
                                  '.bb-recommended, [class*="recommend"], .CartNote, [class*="cart-note"]');
      if (after && after.parentNode === t) t.insertBefore(built.el, after);
      else if (after && t.contains(after)) after.parentNode.insertBefore(built.el, after);
      else t.appendChild(built.el);
      wire(built.el, state);
      /* ⛔ STANDBY ROWS DEFAULT TO HIDDEN. They used to be built visible and rely on their prime to
         hide them, so any path where the prime did not run left a $75 hardware kit or a cap row
         selectable with nothing chosen above it. Failing closed costs an upsell; failing open
         charges for something the shopper never picked. */
      ['cap', 'hw'].forEach(function (k) {
        var s0 = built.el.querySelector('[data-bb-sec="' + k + '"]');
        if (s0) s0.hidden = true;
      });
      /* ⛔ ISOLATED. These were a bare sequence: a synchronous throw in primeConvert/primePosts/
         primeGate meant primeCaps and primeHw never ran at all, and the standby rows stayed on
         screen showing their build-time labels over an empty body. One failure must not cascade,
         and it must be logged rather than vanish. */
      try { primeConvert(built.el, state); } catch (e) { console.log('[bb-cadd] primeConvert failed: ' + (e && e.message)); }
      try { primePosts(built.el, state, built.shortPosts); } catch (e) { console.log('[bb-cadd] primePosts failed: ' + (e && e.message)); }
      /* the ZM gate-post section is built empty - keep it hidden until a gate is owned or picked */
      try { if (state.panels === 0 && state.zmPanels > 0 && !(built.shortPosts > 0)) primeGatePost(built.el, state); }
      catch (e) { console.log('[bb-cadd] primeGatePost failed: ' + (e && e.message)); }
      try { primeGate(built.el, state); } catch (e) { console.log('[bb-cadd] primeGate failed: ' + (e && e.message)); }
      try { primeCaps(built.el, state); } catch (e) { console.log('[bb-cadd] primeCaps failed: ' + (e && e.message)); }
      try { primeSavers(built.el, state); } catch (e) { console.log('[bb-cadd] primeSavers failed: ' + (e && e.message)); }
      try { primeHw(built.el, state); } catch (e) { console.log('[bb-cadd] primeHw failed: ' + (e && e.message)); }
    });
  }

  /* Put a real figure on the posts row before anything is clicked — owner wants the value
     quantified up front, not revealed after a choice. Shows the cheaper option as the "from".
     ⛔ Silent if either product cannot be priced: no figure beats a wrong one. */
  /* ⭐ CAPS. One product, and the only correct variant is decided for the shopper: black (owner),
     sized to the wood post already in the cart. No chips, no selection state — one Add button,
     bound directly, the same shape as the post pick.
     ⛔ If the post width cannot be read, render NOTHING. Guessing a size here ships a cap that
        does not fit a post the shopper has already paid for. */
  /* ⛔ SELECTION ONLY (owner): the cart containing wood posts must NOT reveal this row. It used to,
     and the row then appeared with nothing ticked above it and no cause the shopper could see.
     Passing 0 hides it; only the Wood chip reveals it. Quantity still counts cart posts. */
  function primeCaps(el, state) { renderCaps(el, state, 0); }
  /* widthHint = the width of a post about to be ADDED. Without it this row can only size a cap
     for posts already owned — which is never the case when the row is revealed by picking Wood. */
  function renderCaps(el, state, need, widthHint) {
    var pick = el.querySelector('[data-bb-pick-cap]');
    if (!pick) return;
    var sec = pick.closest('[data-bb-sec]');
    /* ⛔ hide the SECTION, not just the pick — an empty numbered row reads as broken */
    var giveUp = function () { if (sec) sec.hidden = true; };
    if (!(need > 0)) { giveUp(); return; }
    if (sec) sec.hidden = false;
    var lab = el.querySelector('[data-bb-cap-lab]');
    if (lab) lab.textContent = 'Cap your ' + need + ' wood post' + (need === 1 ? '' : 's');
    var short = need;
    getProduct(CAP_HANDLE).then(function (p) {
      /* ⛔ every bail-out from here HIDES the row. The label is written above, before the async
         work, so a failure after it used to leave a numbered header with an empty body — exactly
         what the owner saw. */
      if (!p || !p.variants) { giveUp(); return; }
      var w = widthHint || state.woodPostW || 0;
      if (!w) { giveUp(); return; }
      var v = null;
      p.variants.forEach(function (x) {
        if (v || !x.available) return;
        if (!/black/i.test(x.title)) return;   /* USA stores it lowercase, CA capitalised */
        var sz = parseInt(String(x.title), 10);
        if (sz === w) v = x;
      });
      if (!v) { giveUp(); return; }
      pick.hidden = false;
      pick.innerHTML = '<span class="bb-cadd__note">Keeps water out of the end grain</span>' +
        '<button type="button" class="bb-cadd__optt" data-bb-opt aria-pressed="true">' +
        short + ' &times; ' + w + '&quot; cap — ' + money(v.price * short) + '</button>';
      setItem(el, pick, { id: v.id, quantity: short, price: v.price * short });
    }).catch(function () {});
  }

  /* ⭐ POST SAVERS — the BarrierGuard ground-line sleeve. CEDAR posts only, pre-selected.
     ⛔ CEDAR, NOT ANY WOOD. Guards do not go on metal, and they are not for treated — both
        confirmed by the owner today after I wrongly widened the PDP version to both species.
     ⛔ 10-PACK: one unit covers ten posts, so the quantity is ceil(posts / 10). One per post would
        bill ten times over.
     ⛔ NOMINAL sizing. The option values read `4" x 4" (Nominal 3.5"x3.5")` / `6" x 6" (Nominal
        5.5"x5.5")` since today's relabel, so parseInt gives the nominal 4/6 — the same number the
        post carries, matching the caps matcher. No 3.5/5.5 indirection; that is what once shipped a
        6" sleeve for a 5.5" post. */
  function primeSavers(el, state) { renderSavers(el, state, 0, 0); }
  function renderSavers(el, state, posts, widthHint) {
    var pick = el.querySelector('[data-bb-pick-saver]');
    if (!pick) return;
    var sec = pick.closest('[data-bb-sec]');
    /* ⛔ hide the SECTION on every bail-out — a numbered header with an empty body reads as broken */
    var giveUp = function () { if (sec) sec.hidden = true; };
    if (!(posts > 0)) { giveUp(); return; }
    var w = widthHint || 0;
    if (!w) { giveUp(); return; }
    if (sec) sec.hidden = false;
    var packs = Math.ceil(posts / 10);
    if (packs < 1) packs = 1;
    var lab = el.querySelector('[data-bb-saver-lab]');
    if (lab) lab.textContent = 'Protect your ' + posts + ' cedar post' + (posts === 1 ? '' : 's');
    getProduct(SAVER_HANDLE).then(function (p) {
      if (!p || !p.variants) { giveUp(); return; }
      var v = null;
      p.variants.forEach(function (x) {
        if (v || !x.available) return;
        var sz = parseInt(String(x.title), 10);   /* nominal 4 or 6 */
        if (sz === w) v = x;
      });
      if (!v) { giveUp(); return; }               /* no sleeve for this width — offer nothing */
      pick.hidden = false;
      pick.innerHTML = '<span class="bb-cadd__note">Stops rot at the ground line, where posts fail first</span>' +
        '<button type="button" class="bb-cadd__optt" data-bb-opt aria-pressed="true">' +
        packs + ' &times; 10-pack — ' + money(v.price * packs) + '</button>';
      setItem(el, pick, { id: v.id, quantity: packs, price: v.price * packs });
    }).catch(function () {});
  }

  /* ⭐ HARDWARE. One kit per gate. Same single-variant shape as caps. */
  /* ⛔ SELECTION ONLY, same rule: a gate already in the cart must not reveal the hardware row. */
  function primeHw(el, state) { renderHw(el, state, 0); }
  function renderHw(el, state, need, gateCount) {
    var pick = el.querySelector('[data-bb-pick-hw]');
    if (!pick) return;
    var sec = pick.closest('[data-bb-sec]');
    if (!(need > 0)) { if (sec) sec.hidden = true; return; }
    if (sec) sec.hidden = false;
    var lab = el.querySelector('[data-bb-hw-lab]');
    /* ⛔ plural follows the GATES, not the kits — one double swing needs 2 kits and is still
       one gate. Keying this off `need` said "gates" for a single gate. */
    var nGates = (typeof gateCount === 'number' ? gateCount : state.gates) || 1;
    if (lab) lab.textContent = 'Hardware for your gate' + (nGates === 1 ? '' : 's');
    var short = need;
    getProduct(HW_HANDLE).then(function (p) {
      /* same rule as caps: never leave a numbered header over an empty body */
      var giveUpHw = function () { if (sec) sec.hidden = true; };
      if (!p || !p.variants) { giveUpHw(); return; }
      var v = null;
      p.variants.forEach(function (x) { if (!v && x.available) v = x; });
      if (!v) { giveUpHw(); return; }
      pick.hidden = false;
      pick.innerHTML = '<span class="bb-cadd__note">Hinges, latch and mounting hardware</span>' +
        '<button type="button" class="bb-cadd__optt" data-bb-opt aria-pressed="true">' +
        short + ' &times; kit — ' + money(v.price * short) + '</button>';
      setItem(el, pick, { id: v.id, quantity: short, price: v.price * short });
    }).catch(function () {});
  }

  /* Render the post pick for a given TYPE and (for Wood) a given SPECIES. Lifted out of the
     chip handler so the species chips can re-render without duplicating any of it. */
  function renderPostPick(el, state, type, species) {
    var pick = el.querySelector('[data-bb-pick-post]');
    if (!pick) return;
    var short = state.panels + 1 + state.gatePosts + pendingGatePosts(el) - state.posts;
    pick.hidden = false;
    pick.innerHTML = '<span class="bb-cadd__note">Checking sizes…</span>';
    getProduct(POST_HANDLES[type]).then(function (p) {
      var r = pickPost(p, (state.maxH || 0) + 3, type === 'Wood' ? species : null);
      if (!r) {
        /* ⛔ say we could not SIZE it — never assert the product does not reach. And name the
           SPECIES when one is chosen: "try the other type" pushed people to metal when the real
           answer was that cedar does not stock that length. */
        pick.innerHTML = '<span class="bb-cadd__note">Could not size a ' +
          (type === 'Wood' && species ? species : type.toLowerCase()) +
          ' post for a ' + state.maxH + 'ft panel — try the other ' +
          (type === 'Wood' && species ? 'species' : 'type') + '.</span>';
        return;
      }
      var units = Math.ceil(short / r.per);
      var capW = 0;
      var wm = String(r.v.title || '').match(/(\d+)\s*x\s*\d+\s*x\s*\d+/i);
      if (wm) capW = parseInt(wm[1], 10);
      /* ⛔ METAL => NO CAPS, EVER (owner 2026-09-14: "Metal posts come with their own caps").
         This previously passed state.woodPosts - state.caps under Metal — caps for WOOD posts
         already in the cart — which is why a metal selection could still show "Cap your N wood
         posts". It never proposed a cap FOR a metal post; the owner's rule is simply stricter.
         ⚠️ TRADE-OFF, decided not accidental: a cart holding uncapped WOOD posts is not offered
            caps while Metal is the active pick. Switching to Wood brings the row straight back.
            If that is reported as a bug later, it is this line, and it reverses to
            `: state.woodPosts - state.caps`.
         ⛔ Passing 0 is sufficient: renderCaps hides the section on need <= 0, and the bbRenumber
            observer re-flows the step numbers when it goes. */
      renderCaps(el, state, (type === 'Wood' ? state.woodPosts + units - state.caps : 0),
                 type === 'Wood' ? capW : 0);
      /* ⛔ COUNTS ONLY THE CEDAR POSTS BEING ADDED (`short`), never state.woodPosts: posts already
         in the cart may be pressure treated and the line data does not reliably carry species, so
         including them would sell sleeves for posts that must not have them. Understating is the
         safe direction here. */
      renderSavers(el, state, (type === 'Wood' && species === 'cedar') ? short : 0,
                   (type === 'Wood' && species === 'cedar') ? capW : 0);
      var rating = (type === 'Metal') ? 'Galvanized steel, ground contact'
                 : (speciesOf(r.v.title) === 'cedar' ? 'Cedar, naturally rot resistant'
                                                     : 'UC4A ground-contact rated');
      /* ⭐ THE HINT, and only when it is TRUE — owner: "we have to hint that our posts match the
         wood framing on the panels ... thats a massive upsell". Shown only when the chosen species
         actually equals the run's framing, never as decoration. */
      var fromEl = el.querySelector('[data-bb-post-from]');
      if (fromEl) fromEl.innerHTML = (type === 'Wood' && state.species && species === state.species)
        ? 'Matches your ' + (species === 'cedar' ? 'Cedar' : 'Pressure Treated') + ' framing'
        : '';
      pick.innerHTML = '<span class="bb-cadd__note">' + rating +
        (r.per === 2 ? ' &middot; one ' + lenLabel(r.v) + ' cuts into two' : '') + '</span>' +
        '<span class="bb-cadd__linetot">' + units + ' &times; ' + lenLabel(r.v) +
        ' — ' + money(r.v.price * units) + '</span>';
      setItem(el, pick, { id: r.v.id, quantity: units, price: r.v.price * units });
    });
  }

  /* GATE POST on a ZM-only cart. Mirrors renderHw: hidden until a gate is owned or picked,
     auto-selected (a gate needs exactly one post, so there is no choice to make), and FAILS
     QUIET - a numbered header over an empty body is worse than no prompt at all. */
  function primeGatePost(el, state) { renderGatePost(el, state, 0); }
  function renderGatePost(el, state, need) {
    var pick = el.querySelector('[data-bb-pick-post]');
    if (!pick) return;
    var sec = pick.closest('[data-bb-sec]');
    if (!(need > 0)) { if (sec) sec.hidden = true; return; }
    if (sec) sec.hidden = false;
    var lab = el.querySelector('[data-bb-post-lab]');
    if (lab) lab.textContent = need === 1 ? 'A post for your gate' : need + ' posts for your gates';
    /* must REACH: length = panel height + 3ft. No framed run here, so size off the ZM kit. */
    var reach = (state.maxH || state.zmH || 0) + 3;
    getProduct(POST_HANDLES.Metal).then(function (p) {
      var giveUp = function () { if (sec) sec.hidden = true; };
      if (!p || !p.variants) { giveUp(); return; }
      var r = pickPost(p, reach, state.species);
      if (!r || !r.v) { giveUp(); return; }
      var qty = Math.ceil(need / (r.per || 1));
      pick.hidden = false;
      pick.innerHTML = '<span class="bb-cadd__note">Gate posts are not included with the kit</span>' +
        '<button type="button" class="bb-cadd__optt" data-bb-opt aria-pressed="true">' +
        qty + ' &times; post — ' + money(r.v.price * qty) + '</button>';
      setItem(el, pick, { id: r.v.id, quantity: qty, price: r.v.price * qty });
    }).catch(function () { if (sec) sec.hidden = true; });
  }

  function primePosts(el, state, short) {
    var slot = el.querySelector('[data-bb-post-from]');
    if (!slot || !(short > 0)) return;
    Promise.all([getProduct(POST_HANDLES.Metal), getProduct(POST_HANDLES.Wood)]).then(function (ps) {
      var need = (state.maxH || state.zmH || 0) + 3;
      var m = pickPost(ps[0], need, state.species);
      var w = pickPost(ps[1], need, state.species);
      var opts = [];
      if (w) opts.push(money(w.v.price * Math.ceil(short / w.per)) + ' wood');
      if (m) opts.push(money(m.v.price * Math.ceil(short / m.per)) + ' metal');
      if (!opts.length) return;
      /* ⭐ Peace of mind that these are meant to go IN THE GROUND — owner 2026-08-31. Both claims
         are the store's OWN published PDP wording, not invented here: the metal PDP says "heavy
         gauge galvanized steel", the wood PDP says "ground contact rated UC4A or better". */
      slot.innerHTML = '<span class="bb-cadd__val">' + opts.join(' &middot; ') +
        '</span> · in-ground rated';
    }).catch(function () {});
  }

  /* Same for the gate row: name the product and its real starting price up front. */
  function primeGate(el, state) {
    var sub = el.querySelector('[data-bb-gate-sub]');
    var types = el.querySelector('[data-bb-gate-types]');
    if (!sub || !types) return;
    getGates().then(function (gs) {
      var g = familyGate(state.panelTitle, gs);
      if (!g) return;
      return getProduct(g.handle).then(function (p) {
        /* ⛔ derive from the panel TITLE if state.colour came back empty — a blank colour is what
           let variant order decide, and that is the bug this whole patch exists to close. */
        /* ⛔ PANEL TITLE FIRST. state.colour is accumulated over every cart line and was
           resolving to "brown" on a cart holding nothing but Black Hog Wire Fence Panels.
           state.panelTitle names the product the run is made of and carries the colour on every
           panel product on both stores, so it is the trustworthy source. */
        var gColour = colourOf(state.panelTitle || '') || state.colour;
        var choices = gateChoices(p, state.maxGateH, state.species, gColour).filter(function (c) {
          return !state.gateKinds[c.kind];      /* never re-offer a style already in the cart */
        });
        if (!choices.length) return;
        /* ⛔⛔ COLOUR GUARD — see header. A black run must never be handed a brown gate. */
        if (gColour) {
          choices = choices.map(function (c) {
            if (gateColour(c.v) === gColour) return c;
            var g0 = gateSize(c.v);
            var fix = (p.variants || []).filter(function (v) {
              var g = gateSize(v);
              return v.available && g && g0 && g.h === g0.h &&
                     gateKind(v) === c.kind && gateColour(v) === gColour;
            }).sort(function (a, b) { return a.price - b.price; })[0];
            return fix ? { kind: c.kind, v: fix } : c;
          });
        }
        el.__gate = { product: g, choices: choices };
        types.innerHTML = choices.map(function (c) {
          /* FAMILY-CORRECT THUMBNAIL. familyGate() already chose the right gate product for the
             panel in the cart, so the image comes from THAT product. A hardcoded url showed a
             corrugated gate on a hog wire run. Variant image first so colour matches too. */
          /* ⛔ NEVER fall straight back to the product image — it is whatever colour was photographed
           first (a SILVER gate on CA), so a correctly-chosen black variant rendered silver. Most CA
           black variants carry no image of their own. Borrow from a same-colour sibling first. */
        var gi = (c.v && c.v.featured_image && c.v.featured_image.src) || '';
        if (!gi && c.v) {
          var wantCol = gateColour(c.v);
          var sib = (p.variants || []).filter(function (x) {
            return x.featured_image && x.featured_image.src && gateColour(x) === wantCol;
          })[0];
          if (sib) gi = sib.featured_image.src;
        }
        gi = gi || p.featured_image || '';
          if (gi) gi = String(gi).split('?')[0] + '?width=120';
          /* ⭐ NAME THE COLOUR. Same gateColour() the filter uses — a second derivation could
             print a reassuring label over a wrong pick. Owner could not tell a mis-pick from a
             misleading 46px photo, and neither could I. */
          var cn = gateColour(c.v);
          if (cn === 'rust') cn = 'corten';
          cn = cn ? cn.charAt(0).toUpperCase() + cn.slice(1) : '';
          return '<button type="button" class="bb-cadd__t" data-bb-gate-kind="' + c.kind + '"' +
                 (gi ? ' style="--bb-ti:url(' + gi + ')"' : '') + '>' +
                 c.kind +
                 (cn ? '<span class="bb-cadd__tc">' + cn + '</span>' : '') + '</button>';
        }).join('');
        /* ⭐ Owner 2026-08-31: gate hardware is INCLUDED — say so. It is the first question a
           buyer asks about a gate, and leaving it unsaid invites a support email or a shopper
           hunting for a hinge kit that is already in the box. */
        /* ⭐ WELDED is TRUE for all-metal gates and ONLY for them (owner 2026-08-31: "for the
           ZM kits we genuinely weld the all metal gates"). Treated-timber and cedar frames are
           fastened, not welded, so the claim is gated on the picked variant carrying a full metal
           frame. Saying it everywhere would be false on most of the range; saying it nowhere threw
           away the strongest thing about the metal ones. */
        var allMetal = choices.every(function (c) { return /full metal|metal frame/i.test(c.v.title); });
        /* ⭐ state the matched colour here too, so the line reads "4ft · Black · from $274.99".
           Taken from the CHOICES, never from state.colour — this must report what was actually
           picked, not what we asked for. When the choices disagree it says nothing rather than
           picking one to display. */
        var cols = choices.map(function (c) { var x = gateColour(c.v); return x === 'rust' ? 'corten' : x; })
          .filter(function (x, i, a) { return x && a.indexOf(x) === i; });
        var colTxt = cols.length === 1 ? (cols[0].charAt(0).toUpperCase() + cols[0].slice(1)) : '';
        sub.innerHTML = state.maxGateH + 'ft &middot; ' + (colTxt ? colTxt + ' &middot; ' : '') +
          '<span class="bb-cadd__val">from ' +
          money0(Math.min.apply(null, choices.map(function (c) { return c.v.price; }))) +
          '</span> &middot; ' + (allMetal ? 'welded steel frame &middot; ' : '') + 'hardware sold separately';
      });
    }).catch(function () {});
  }

  /* Fill the conversion headline with the REAL all-in number as soon as it can be priced, so the
     hook is visible before the shopper clicks anything. Owner: "you have to hook people somehow".
     ⛔ Silent on failure — the section keeps its benefit-only wording rather than showing a
        guessed or zero figure. A wrong saving is worse than no saving. */
  /* BB-CONV-VAR — paint each conversion chip from the variant it actually maps to.
     Variant image first so the frame/species shown matches what would be added; product image as
     the fallback. Never a hardcoded url: the chip must follow the cart, not a guess. This is the
     same rule primeGate() already follows for the gate chips. */
  function paintConvTiles(el, state) {
    var src = (state.loose && state.loose.length) ? state.loose : state.framed;
    if (!src || !src.length) return;
    getProduct(src[0].handle).then(function (p) {
      if (!p || !p.variants) return;
      var cur = p.variants.filter(function (v) { return v.id === src[0].id; })[0];
      if (!cur) return;
      var fi = -1;
      (p.options || []).forEach(function (o, i) {
        var n = (typeof o === 'string') ? o : o.name;
        if (/frame/i.test(n)) fi = i;
      });
      if (fi < 0) return;
      var WANT = { treated: /treated/i, cedar: /cedar/i, unframed: /unframed/i };
      Object.keys(WANT).forEach(function (k) {
        var btn = el.querySelector('[data-bb-conv="' + k + '"]');
        if (!btn) return;
        var t = p.variants.filter(function (v) {
          for (var i = 0; i < cur.options.length; i++) {
            if (i === fi) continue;
            if (v.options[i] !== cur.options[i]) return false;   /* hold size + opening */
          }
          return WANT[k].test(String(v.options[fi] || ''));
        })[0];
        var u = (t && t.featured_image && t.featured_image.src) || p.featured_image || '';
        if (!u) return;
        btn.style.setProperty('--bb-ti', 'url(' + String(u).split('?')[0] + '?width=120)');
      });
    })['catch'](function () { /* leave chips unpainted rather than break the drawer */ });
  }

  function primeConvert(el, state) {
    var lab = el.querySelector('[data-bb-conv-lab]');
    var sub = el.querySelector('[data-bb-conv-sub]');
    if (!lab) return;
    var want = state.loose.length ? 'treated' : (state.framed.length ? 'unframed' : null);
    if (!want) return;
    paintConvTiles(el, state);
    var lq = state.loose.reduce(function (a, l) { return a + l.qty; }, 0);

    planConvert(state, want).then(function (plan) {
      if (!plan || !plan.items.length) return;
      paintConvert(el, plan, want);
    }).catch(function () { /* keep the benefit-only wording */ });
  }

  function run() {
    if (busy) return;
    busy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        busy = false;
        var st = readCart(c);
        if (!st) return;
        if (!st.count) { setDismissed(false); clearSecs(); return; }
        /* ⛔ BB-MINORDER-FIXED — NO subtotal gate, and that is correct. This used to bail out whenever the
           subtotal was under the minimum constant (written without the literal so the deploy
           guard can still assert the real line is gone), which was backwards even then: the same
           drawer was showing a freight bar telling the shopper to add more, and this is the only
           component that knows WHAT to add. Suppressing it left them with a demand and no way to
           meet it.
           ⚠️ The floor itself is now GONE — the four-band card has no minimum — so the original
           justification is void. Removing the gate was right regardless. Corrected 2026-10-06. */
        injectCss();
        mount(st);
      })
      .catch(function () { busy = false; });
  }

  var t = null;
  function schedule() { clearTimeout(t); t = setTimeout(run, 250); }

  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].type !== 'childList') continue;
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n.nodeType !== 1) continue;
          if (n.classList && (n.classList.contains('Drawer__Main') || n.classList.contains('Cart'))) { schedule(); return; }
          if (n.querySelector && n.querySelector('.Drawer__Main, .CartItem')) { schedule(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();

/* ===== bb-bundle-mobile-collapsed 2026-10-03 =============================================================
   Owner: on mobile the bundle should start collapsed, and "Nothing selected" should only show
   when it is open.
   The collapse state already exists (data-min="1" on .bb-cadd). This just sets it by default on
   phones and keeps the toggle button in sync, rather than splicing the root element, which is
   built from one long string concat.
   ⚠️ The addons block is injected and re-injected as the cart changes, so this watches for it
      instead of running once. Each element is only touched once (data-bb-mobinit). ======== */
(function () {
  function isPhone() {
    try { return window.matchMedia && window.matchMedia("(max-width:768px)").matches; }
    catch (e) { return false; }
  }
  function collapse(el) {
    if (!el || el.getAttribute("data-bb-mobinit") === "1") return;
    el.setAttribute("data-bb-mobinit", "1");
    /* ⭐ OPEN ON LOAD (owner 2026-10-04). This used to collapse the block on phones; now
       that it is the "Ready to Install" upsell it opens with the drawer. Only the automatic
       default is gone — the manual +/- toggle and tap-to-open still work. */
    return;
    var b = el.querySelector("[data-bb-min]");
    if (b) {
      b.setAttribute("aria-expanded", "false");
      b.setAttribute("title", "Show");
      b.innerHTML = "&plus;";
    }
  }
  function scan() {
    var list = document.querySelectorAll(".bb-cadd");
    for (var i = 0; i < list.length; i++) collapse(list[i]);
  }
  try { new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true }); }
  catch (e) {}
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scan);
  else scan();
})();
